import type { ChangeEvent, FunctionComponent } from 'react';
import React, { useEffect, useRef } from 'react';

import { Col, Row } from 'src/components/ui';
import Form from 'src/components/ui/Form';

import type {
  CustomValidityMessages,
  UpdateFieldType,
} from '../contactForm/ContactForm';
import style from '../contactForm/ContactForm.module.scss';

import type { ContactFormData } from 'src/models/contact';

import { INVALID_INPUT_FORMAT } from 'src/utils/Message.Utils';
import { mergeClassNames } from 'src/utils/ReactUtils';

type RequestInformationFormType = {
  updateField: UpdateFieldType;
  formValue: ContactFormData;
  customValidityMessages: CustomValidityMessages;
};

const RequestInformationForm: FunctionComponent<RequestInformationFormType> = ({
  updateField,
  formValue,
  customValidityMessages,
}) => {
  const registrationRef = useRef<HTMLInputElement>(null);
  const orderNumberRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      registrationRef.current &&
      formValue.registration &&
      customValidityMessages.registration !== ''
    ) {
      registrationRef.current.setCustomValidity(INVALID_INPUT_FORMAT);
    }
  }, [formValue.registration, customValidityMessages.registration]);

  useEffect(() => {
    if (
      orderNumberRef.current &&
      formValue.command &&
      customValidityMessages.command !== ''
    ) {
      orderNumberRef.current.setCustomValidity(INVALID_INPUT_FORMAT);
    }
  }, [formValue.command, customValidityMessages.command]);

  return (
    <>
      <Row>
        {formValue.requestType === 1 && (
          <Col md={6}>
            <Form.Group controlId="registration">
              <Form.Label
                className={mergeClassNames([
                  style.contactFormLabel,
                  style.contactFormLabelRequired,
                ])}
              >
                Immatriculation de votre véhicule
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="AA-123-BB *"
                required
                onChange={updateField}
                value={formValue.registration}
                name="registration"
                ref={registrationRef}
              />
              <Form.Control.Feedback type="invalid">
                {formValue.registration &&
                customValidityMessages.registration !== ''
                  ? customValidityMessages.registration
                  : 'Ce champ est obligatoire.'}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}
        {formValue.subRequestType === 1 && (
          <Col md={6}>
            <Form.Group controlId="command">
              <Form.Label
                className={mergeClassNames([
                  style.contactFormLabel,
                  style.contactFormLabelRequired,
                ])}
              >
                Numéro de votre commande
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="AD-AUTO-XXXXXXXX-XXXXXX *"
                required
                onChange={updateField}
                value={formValue.command}
                name="command"
                ref={orderNumberRef}
              />
              <Form.Control.Feedback type="invalid">
                {formValue.command && customValidityMessages.command !== ''
                  ? customValidityMessages.command
                  : 'Ce champ est obligatoire.'}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={12}>
          <Form.Group controlId="subject">
            <Form.Label
              className={mergeClassNames([
                style.contactFormLabel,
                style.contactFormLabelRequired,
              ])}
            >
              Objet de votre demande
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Objet de votre demande *"
              required
              pattern={'^.{0,199}$'}
              onChange={updateField}
              value={formValue.subject}
              name="subject"
            />
            <Form.Control.Feedback type="invalid">
              {!formValue?.subject?.length
                ? 'Ce champ est obligatoire.'
                : 'Le format est invalide'}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Form.Group controlId="description">
            <Form.Label
              className={mergeClassNames([
                style.contactFormLabel,
                style.contactFormLabelRequired,
              ])}
            >
              Description de votre demande
            </Form.Label>
            <Form.Control
              placeholder="Description de votre demande *"
              required
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                const { value } = event.target;
                if (value.length < 20 || value.length > 500) {
                  event.target.setCustomValidity(INVALID_INPUT_FORMAT);
                } else {
                  event.target.setCustomValidity('');
                }
                updateField(event);
              }}
              as="textarea"
              rows={3}
              value={formValue.description}
              name="description"
            />
            <Form.Control.Feedback type="invalid">
              {!formValue?.description?.length
                ? 'min 20 caractères - max 500 caractères'
                : 'Le format est invalide'}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default RequestInformationForm;
